import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { SumPipe } from '@pg-app/partner/dashboard/components/_pipes/sum.pipe';
import { TableDialogModel } from '@pg-app/partner/dashboard/model/table-dialog.model';
import { TableDialogService } from '@pg-app/partner/dashboard/services/table-dialog.service';
import * as d3 from 'd3';
import { timer } from 'rxjs';

@Component({
  selector: 'np-donut-chart',
  styleUrls: ['./donut-chart.component.scss'],
  templateUrl: './donut-chart.component.html'
})

export class DonutChartComponent implements OnInit, OnChanges {
  @ViewChild('containerPieChart', {static: true}) chartContainer: ElementRef;
  @Input() data: any;

  hostElement: any;
  svg: any;
  radius: number;
  innerRadius: number;
  outerRadius: number;
  arcGenerator: any;
  arcHover: any;
  pieGenerator: any;
  path: any;
  values: Array<number>;
  labels: Array<string>;
  tooltip: any;
  pieColours: any;
  slices: Array<any>;
  selectedSlice: any;
  colourSlices: Array<string> = ['#3c455c', '#9aabb8', '#dde1e2'];
  duration = 750;
  mostRecentActivityColor = '#00b600';
  mostRecentActivityLabel = 'Recent activity';

  constructor(
    private elRef: ElementRef,
    private tableDialogService: TableDialogService
  ) { }

  ngOnInit() {
    timer(100).subscribe(() => {
      this.createChart();
    });
  }

  @HostListener('window:resize')
  onresize() {
    this.removeChart();
    this.createChart();
    this.updateChart();
  }

  ngOnChanges() {
    if (this.svg) {
      this.updateChart();
    }
  }

  removeChart() {
    d3.select(this.hostElement)
      .selectAll('svg')
      .remove();
  }

  wrap(text, width) {
    text.each(function () {
      text = d3.select(this);
      const words = text.text().split(/\s+/).reverse();
      let word;
      let line = [];
      let lineNumber = 2;
      const lineHeight = 5;
      const y = text.attr('y');
      const dy = parseFloat(text.attr('dy'));
      let tspan = text.text(null).append('tspan').attr('x', 0).attr('y', y).attr('dy', dy + 'px');
      while (word = words.pop()) {
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = text.append('tspan').attr('x', 0).attr('y', y).attr('dy', ++lineNumber * lineHeight + dy + 'px').text(word);
        }
      }
    });
  }
  createChart = () => {
    this.hostElement = this.chartContainer.nativeElement;
    this.radius = 90;
    const mostRadius = 40;
    const innerRadius = this.radius - 35;
    const outerRadius = this.radius - 10;
    const hoverRadius = this.radius - 15;

    this.tooltip = this.elRef.nativeElement.querySelector('.tooltip');

    this.pieGenerator = d3.pie().sort(null).value((d: number) => d)([0, 0, 0]);

    this.arcGenerator = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .cornerRadius(0)
      .padAngle(0);

    this.arcHover = d3.arc().innerRadius(innerRadius).outerRadius(hoverRadius);

    this.svg = d3.select(this.hostElement).append('svg')
      .attr('viewBox', '0, 0, ' + this.hostElement.offsetWidth + ', ' + 240)
      .attr('height', 240)
      .attr('width', this.hostElement.offsetWidth)
      .append('g')
      .attr('transform', `translate(${this.hostElement.offsetWidth / 2}, ${this.hostElement.offsetHeight / 2})`);

    this.svg.selectAll('.toolCircle').exit().remove();

    const toolCircle = this.svg.append('circle')
      .attr('class', 'toolCircle')
      .attr('r', mostRadius)
      .attr('id', 'chart-label')
      .attr('opacity', .7)
      .style('fill', this.mostRecentActivityColor)
      .on('mouseover', () => {
        this.selectedSlice = { label: this.mostRecentActivityLabel, value: 100 };
        toolCircle.attrs({
          'z-index': '2',
          'white-space': 'wrap',
          'transition': 'all 1000ms ease-in',
          'transform': 'scale(1.5)'
        });
      })
      .on('mouseout', () => {
        toolCircle.attrs({
          'z-index': '',
          'transition': '',
          'transform': ''
        });
      })
      .on('click', () => { this.showDialog(); });

    this.svg.selectAll('.label').exit().remove();

    this.svg.append('text')
      .attr('class', 'label')
      .style('text-anchor', 'middle')
      .style('font-size', '1.2rem')
      .style('cursor', 'default')
      .style('transition', 'all 1000ms ease-in')
      .attr('x', 10)
      .attr('y', 5)
      .attr('dy', '-10px')
      .style('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('lengthAdjust', 'spacingAndGlyphs')
      .text(this.mostRecentActivityLabel)
      .on('mouseover', () => {
        toolCircle.attrs({
          'z-index': '2',
          'transition': 'all 1000ms ease-in',
          'transform': 'scale(1.5)'
        });
      })
      .on('mouseout', () => {
        toolCircle.attrs({
          'z-index': '',
          'transition': '',
          'transform': ''
        });
      })
      .call(this.wrap, 70);
  }

  updateChart = () => {
    const vm = this;
    this.slices = this.data || [];
    this.labels = this.slices.map(slice => slice.label);
    this.values = this.slices.map(slice => slice.value);
    this.pieGenerator = d3.pie().value((d: number) => d).sort(null)(this.values);
    this.pieColours = d3.scaleOrdinal().range(this.colourSlices);

    // path
    const arc = this.svg.selectAll('.arc').data(this.pieGenerator);
    arc.exit().remove();

    const arcEnter = arc.enter().append('g').attr('class', 'arc');
    arcEnter.append('path')
      .attr('d', this.arcGenerator)
      .each((values) => values)
      .on('mouseover', this.mouseover)
      .on('mouseout', this.mouseout)
      .on('click', (d) => { this.showDialog(); });

    d3.select(this.hostElement).selectAll('path')
      .data(this.pieGenerator)
      .attr('fill', (datum, index) => this.pieColours(this.labels[index]))
      .attr('d', this.arcGenerator)
      .transition()
      .duration(this.duration)
      .attrTween('d', function (newValues, i) {
        return vm.arcTween(newValues, i, this);
      });

    // add text labels
    const arcLabelName = this.svg.selectAll('.labelName').data(this.pieGenerator);
    arcLabelName.exit().remove();

    const arcLabelNameEnter = arc.enter().append('g').attr('class', 'labelName hidden-xs');
    arcLabelNameEnter.append('text').attr('d', this.arcGenerator);
    d3.select(this.hostElement).selectAll('.labelName text')
      .data(this.pieGenerator)
      .attr('d', this.arcGenerator)
      .html(function (d) {
        if (d['value'] === 0) {
          return '';
        }
        return ' ' + d['value']
          + '<tspan style="font-size: 1rem"> ('
          + vm.toPercent(d['value'], new SumPipe().transform(vm.values))
          + ')</tspan>';
      })
      .transition()
      .duration(this.duration)
      .attrTween('d', function (newValues, i) {
        return vm.arcTween(newValues, i, this);
      })
      .attr('transform', function (d) {
        const pos = vm.arcHover.centroid(d);
        pos[0] = vm.midAngle(d) < Math.PI ? (vm.radius * 0.9 + 10) : vm.radius * 0.9 * -1;
        return 'translate(' + pos + ')';
      })
      .style('text-anchor', function (d) {
        return (vm.midAngle(d)) < Math.PI ? 'start' : 'end';
      });

    // add text line
    const arcLine = this.svg.selectAll('.line').data(this.pieGenerator);
    arcLine.exit().remove();

    const arcLineEnter = arc.enter().append('g').attr('class', 'line hidden-xs');
    arcLineEnter.append('polyline').attr('points', function (d) {
      if (d.value === 0) {
        return '';
      }
      const pos = vm.arcHover.centroid(d);
      pos[0] = vm.midAngle(d) < Math.PI ? (vm.radius * 0.9 + 10) : vm.radius * 0.9 * -1;
      return [vm.arcGenerator.centroid(d), vm.arcHover.centroid(d), pos].toString();
    })
      .style('opacity', '0.3')
      .style('stroke', 'black')
      .style('stroke-width', '1px')
      .style('fill', 'none');

    d3.select(this.hostElement)
      .selectAll('polyline')
      .data(this.pieGenerator)
      .transition()
      .duration(this.duration);
  }

  private midAngle(d: any) {
    return d.startAngle + (d.endAngle - d.startAngle) / 2;
  }

  private arcTween(newValues, i, slice) {
    const interpolation = d3.interpolate(slice.storedValues, newValues);
    slice.storedValues = interpolation(0);
    return (t) => {
      return this.arcGenerator(interpolation(t));
    };
  }

  private mouseover = (d, i) => {
    this.selectedSlice = d ? this.slices[i] : { label: this.mostRecentActivityLabel, value: 100 };
    const doc = document.documentElement;
    const left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    const clientY = d3.event.pageY;
    const clientX = d3.event.pageX;

    setTimeout(() => {
      this.tooltip.style.visibility = 'visible';
      this.tooltip.style.opacity = 0.9;
      this.tooltip.style.top = clientY - this.tooltip.offsetHeight - top - 7 + 'px';
      this.tooltip.style.left = clientX - this.tooltip.offsetWidth / 2 - left + 'px';
    });
  }

  private mouseout = () => {
    this.tooltip.style.visibility = 'hidden';
    this.tooltip.style.opacity = 0;
  }

  private toPercent = (a: number, b: number): string => {
    return (a / b * 100).toFixed(2) + '%';
  }

  public showDialog() {
    if (this.selectedSlice) {
      this.tableDialogService.showDialog(new TableDialogModel(this.selectedSlice.label, null));
    }
  }

}
